import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  AppAnalysisStatus,
  AppAnalysisRunLogLevel,
  QlikEvaluationStatus,
  StartAppAnalysisInput,
} from "@repo/shared";
import {
  compareAppAnalyses,
  getAppAnalysis,
  listAppAnalyses,
  listAppAnalysisRunLogs,
  listQlikEvaluationRanking,
  deleteAppAnalysis,
  startAppAnalysis,
} from "@/lib/api/app-analysis";

// A lifecycle analysis is "active" only while processing (failed/cancelled/
// completed are terminal). Drives conditional polling.
export function isAppAnalysisActive(status: AppAnalysisStatus | undefined): boolean {
  return status === "processing";
}

// Qlik queues its evaluation on its own side and it outlives our analysis — the
// probed app took 10 min 55 s, long after the static result was saved. So the
// detail keeps polling for the evaluation after the analysis itself is
// 'completed'.
export function isQlikEvaluationPending(status: QlikEvaluationStatus | undefined): boolean {
  return status === "pending" || status === "running";
}

export const appAnalysisKeys = {
  all: ["app-analysis"] as const,
  lists: () => [...appAnalysisKeys.all, "list"] as const,
  list: (filters?: {
    connectionId?: string;
    appId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    status?: AppAnalysisStatus;
  }) => [...appAnalysisKeys.lists(), filters] as const,
  details: () => [...appAnalysisKeys.all, "detail"] as const,
  detail: (id: string) => [...appAnalysisKeys.details(), id] as const,
  logs: (id: string, filters?: { level?: AppAnalysisRunLogLevel }) =>
    [...appAnalysisKeys.all, "logs", id, filters ?? {}] as const,
  compare: (id: string, baseId: string) => [...appAnalysisKeys.all, "compare", id, baseId] as const,
  evaluationRanking: (connectionId: string) =>
    [...appAnalysisKeys.all, "evaluation-ranking", connectionId] as const,
};

export const appAnalysisListQueryOptions = (params?: {
  connectionId?: string;
  appId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  status?: AppAnalysisStatus;
}) =>
  queryOptions({
    queryKey: appAnalysisKeys.list({
      connectionId: params?.connectionId,
      appId: params?.appId,
      dateFrom: params?.dateFrom,
      dateTo: params?.dateTo,
      limit: params?.limit,
      status: params?.status,
    }),
    queryFn: () => listAppAnalyses(params),
    enabled: params !== undefined,
    // Poll while any listed analysis is still processing so placeholder rows flip
    // to completed/failed live.
    refetchInterval: (query) =>
      query.state.data?.data.some((a) => isAppAnalysisActive(a.status)) ? 5000 : false,
  });

// The list screen's flavor of the list: the newest `limit` analyses, plus the
// cursor pages behind them, pulled on demand ("load older analyses"). The API
// only filters by one connection and a date range; the rest (app name, several
// connections) narrows what is loaded, on the client. The query never reads on
// its own beyond the pages the person asked for.
export const appAnalysisListInfiniteQueryOptions = (params: {
  connectionId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}) =>
  infiniteQueryOptions({
    queryKey: [...appAnalysisKeys.lists(), "infinite", params] as const,
    queryFn: ({ pageParam, signal }) =>
      listAppAnalyses({ ...params, cursor: pageParam || undefined }, { signal }),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (query) => {
      const hasActive = query.state.data?.pages.some((p) =>
        p.data.some((a) => isAppAnalysisActive(a.status)),
      );
      return hasActive ? 5000 : false;
    },
  });

export const appAnalysisDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: appAnalysisKeys.detail(id),
    queryFn: () => getAppAnalysis(id),
    enabled: !!id,
    // Two clocks, two cadences: the analysis is ours and finishes in seconds
    // (2 s), Qlik's evaluation is queued on their side and takes minutes (15 s
    // is plenty, and the read is a plain DB projection). Polling has to key off
    // the evaluation too — the analysis goes 'completed' while it is still
    // running, and without this the badge would sit on "waiting for Qlik"
    // until the user reloaded the page.
    refetchInterval: (query) => {
      const detail = query.state.data;
      if (isAppAnalysisActive(detail?.status)) return 2000;
      if (isQlikEvaluationPending(detail?.qlikEvaluation?.status)) return 15000;
      return false;
    },
  });

// Cross-app Qlik performance ranking for one connection. Only completed
// evaluations are ranked, so nothing here changes while the page is open.
export const qlikEvaluationRankingQueryOptions = (connectionId: string) =>
  queryOptions({
    queryKey: appAnalysisKeys.evaluationRanking(connectionId),
    queryFn: () => listQlikEvaluationRanking(connectionId),
    enabled: !!connectionId,
  });

// Diff between two completed analyses of the same app. Immutable once both
// analyses are completed, so it never needs to refetch.
export const appAnalysisCompareQueryOptions = (id: string, baseId: string) =>
  queryOptions({
    queryKey: appAnalysisKeys.compare(id, baseId),
    queryFn: () => compareAppAnalyses(id, baseId),
    enabled: !!id && !!baseId,
    staleTime: Infinity,
  });

// Chronological worker execution-log stream. The level filter runs server-side
// (the query key carries it, so a change refetches from page 1); while the
// analysis is active the loaded pages poll so new lines stream in live.
export const appAnalysisRunLogsInfiniteQueryOptions = (
  id: string,
  filters: { level?: AppAnalysisRunLogLevel },
  active: boolean,
) =>
  infiniteQueryOptions({
    queryKey: appAnalysisKeys.logs(id, filters),
    queryFn: ({ pageParam }) =>
      listAppAnalysisRunLogs(id, {
        cursor: pageParam as string | undefined,
        limit: 200,
        level: filters.level,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!id,
    refetchInterval: active ? 5000 : false,
  });

export function useStartAppAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StartAppAnalysisInput) => startAppAnalysis(input),
    onSuccess: (analysis) => {
      queryClient.setQueryData(appAnalysisKeys.detail(analysis.id), analysis);
      queryClient.invalidateQueries({ queryKey: appAnalysisKeys.lists() });
    },
  });
}

export function useDeleteAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAppAnalysis(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appAnalysisKeys.lists() });
    },
  });
}
